/**
 * Filtre prix en champs De/À : pont entre deux champs de saisie libre et les
 * paramètres Shopify `filter.*.gte` / `filter.*.lte`, portés par deux
 * <input type="hidden"> — même principe que le filtre par tranches qu'il remplace.
 *
 * Le point dur n'est pas l'écriture des champs, c'est le MOMENT de la
 * soumission. Le bloc est rendu dans #CollectionAjaxContent, dont
 * l'innerHTML entier est remplacé à chaque filtrage (theme.js, rendu AJAX) :
 * soumettre à chaque frappe arracherait le champ « À » sous les doigts de
 * l'utilisateur en train de le remplir. La requête ne part donc qu'une
 * fois, quand la saisie du bloc est terminée (perte de focus, Entrée, ou
 * clic sur « Appliquer » dans le drawer mobile).
 *
 * HTML contract (celui rendu par T5/T6, cf. plan §T5) :
 *   <div data-range-filter
 *        data-range-min="0" data-range-max="15000"
 *        data-committed="1000|4000">   -- rendu par Liquid, jamais posé ici
 *     <input type="number" data-range-input data-range-field="gte">
 *     <input type="number" data-range-input data-range-field="lte">
 *     <input type="hidden" data-range-hidden-gte name="filter.v.price.gte" disabled>
 *     <input type="hidden" data-range-hidden-lte name="filter.v.price.lte" disabled>
 *   </div>
 * `data-range-input` est un attribut NU (même rôle sur les deux champs) —
 * c'est `data-range-field` ("gte"/"lte") qui distingue « De » de « À ». Les
 * hidden portent le rôle dans le NOM de l'attribut (`data-range-hidden-gte`
 * / `-lte`, sans valeur), pas dans une valeur d'attribut — même convention
 * que le filtre par tranches qu'il remplace (`data-bucket-hidden-lte`/`-gte`).
 *
 * `data-committed` (format "gte|lte", en euros, chaîne vide de part et
 * d'autre du "|" pour une borne inactive) est LA seule source de vérité :
 * rendue par Liquid, elle redevient juste à chaque rendu AJAX quoi que ce
 * script ait écrit avant. Ce script ne l'initialise JAMAIS ; il ne la
 * réécrit qu'au moment d'émettre une soumission. C'est une marque
 * OPTIMISTE : elle est posée avant que la requête ait abouti, donc si le
 * rendu échoue (theme.js ne pose aucun .catch sur renderCollectionPage),
 * le bloc porte un état réputé appliqué qui ne l'est pas, et la sortie
 * suivante du champ n'émettra rien. Si l'attribut est absent, l'état est
 * traité comme inconnu et on soumet — une requête de trop vaut mieux qu'un
 * filtre qui ne part jamais.
 *
 * `data-range-min` / `data-range-max` sont des bornes de CLAMPING, pas une
 * source de vérité : absentes ou non numériques, elles signifient « aucune
 * borne » (`clamp()` laisse alors la valeur saisie telle quelle) — ce n'est
 * jamais une erreur bloquante.
 */
(function () {
  'use strict';

  /**
   * Normalise puis convertit une saisie en nombre.
   *
   * Accepte les formats FR usuels : `\s` couvre déjà l'espace normal ET
   * l'espace insécable/fine (U+00A0, U+202F — vérifié, ce n'est pas une
   * coïncidence de moteur), donc "1 500" (espace normal ou insécable,
   * pavé numérique iOS notamment) est un simple retrait d'espaces ; la
   * virgule décimale FR ("1500,50") est ensuite convertie en point.
   *
   * Refuse tout ce qui n'est pas un nombre décimal simple ensuite — la
   * notation scientifique ("1e21"), hexadécimale ("0x10"), ou les littéraux
   * Infinity/NaN : `Number()` les accepte tous, alors qu'aucun n'est une
   * valeur `filter.v.price.gte`/`.lte` exploitable une fois sérialisée
   * (`String(1e21) === "1e+21"`, `String(NaN) === "NaN"`). Le filtre prix
   * n'est PAS entier — ce catalogue a des prix à centimes (ex. X99,90 €) —
   * donc aucun arrondi ici, seule la FORME est validée.
   */
  function toNumber(raw) {
    if (raw === null || typeof raw === 'undefined') return null;
    var normalized = String(raw).replace(/\s/g, '').replace(',', '.');
    if (normalized === '') return null;
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
    var n = Number(normalized);
    return isFinite(n) ? n : null;
  }

  function clamp(num, min, max) {
    if (num === null) return null;
    if (min !== null && num < min) return min;
    if (max !== null && num > max) return max;
    return num;
  }

  function writeHidden(hidden, num) {
    if (num === null) {
      hidden.disabled = true;
      hidden.value = '';
    } else {
      hidden.disabled = false;
      hidden.value = String(num);
    }
  }

  /**
   * Recompose l'état, le compare à `data-committed`, et n'émet une
   * soumission que si l'état a réellement changé depuis le dernier rendu
   * serveur — sinon ouvrir puis refermer le panneau relancerait un rendu
   * AJAX complet pour rien.
   */
  function commit(bloc) {
    if (!bloc.isConnected) return;

    var gteInput = bloc.querySelector('[data-range-field="gte"]');
    var lteInput = bloc.querySelector('[data-range-field="lte"]');
    var gteHidden = bloc.querySelector('[data-range-hidden-gte]');
    var lteHidden = bloc.querySelector('[data-range-hidden-lte]');
    if (!gteInput || !lteInput || !gteHidden || !lteHidden) return;

    var boundsMin = toNumber(bloc.getAttribute('data-range-min'));
    var boundsMax = toNumber(bloc.getAttribute('data-range-max'));

    var gte = clamp(toNumber(gteInput.value), boundsMin, boundsMax);
    var lte = clamp(toNumber(lteInput.value), boundsMin, boundsMax);

    if (gte !== null && lte !== null && gte > lte) {
      var swap = gte;
      gte = lte;
      lte = swap;
    }

    // Les champs visibles reflètent toujours la valeur normalisée, y
    // compris quand l'utilisateur n'a rien changé (échange gte/lte inclus).
    gteInput.value = gte === null ? '' : String(gte);
    lteInput.value = lte === null ? '' : String(lte);
    writeHidden(gteHidden, gte);
    writeHidden(lteHidden, lte);

    var etat = (gte === null ? '' : String(gte)) + '|' + (lte === null ? '' : String(lte));

    // Comparaison NUMÉRIQUE, pas textuelle : T5/T6 rendent `data-committed`
    // via `divided_by: 100.0` (diviseur flottant, cf. plan) pour préserver
    // les centimes — rien ne garantit que la représentation texte d'un
    // flottant Ruby ("1000.0") coïncide avec `String(Number(x))` en JS
    // ("1000"). Une égalité de chaînes échouerait alors à CHAQUE commit, et
    // le garde anti-rendu-inutile ne garderait plus jamais rien.
    var committedRaw = bloc.getAttribute('data-committed');
    if (committedRaw !== null) {
      var committedParts = committedRaw.split('|');
      var committedGte = toNumber(committedParts[0]);
      var committedLte = toNumber(committedParts[1]);
      if (committedGte === gte && committedLte === lte) return;
    }

    bloc.setAttribute('data-committed', etat);
    // Un seul dispatch suffit : le hidden et son jumeau appartiennent au même
    // <form>, le listener bulle de theme.js (initFilters) réagit au premier
    // `input` qui l'atteint — en dispatcher deux enverrait deux requêtes.
    gteHidden.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * 1. Écriture brute à chaque frappe, en CAPTURE.
   * La capture s'exécute avant le listener bulle posé par initFilters
   * (theme.js:5241) sur le <form> : sans stopPropagation, chaque touche
   * soumettrait le filtre (même parade que le filtre par tranches remplacé).
   * Aucune normalisation ici : clamper à chaque touche empêcherait de
   * taper "1" dans un champ dont le minimum autorisé est 500. La borne ne
   * se pose qu'à la validation (commit).
   */
  document.addEventListener(
    'input',
    function (e) {
      var input = e.target.closest('[data-range-input]');
      if (!input) return;
      var bloc = input.closest('[data-range-filter]');
      if (!bloc) return;

      e.stopPropagation();

      var role = input.getAttribute('data-range-field'); // "gte" ou "lte"
      var hidden = bloc.querySelector('[data-range-hidden-' + role + ']');
      if (!hidden) return;

      var raw = input.value;
      if (raw.trim() === '') {
        hidden.disabled = true;
        hidden.value = '';
      } else {
        hidden.disabled = false;
        hidden.value = raw;
      }
    },
    true,
  );

  /**
   * 2. Perte de focus : commit différé.
   * setTimeout(…, 0) laisse le focus atterrir sur le tour de boucle suivant
   * avant de juger la saisie terminée — sinon un passage « De » → « À »
   * committerait (et soumettrait) à chaque tabulation entre les deux
   * champs. Un seul contrôle en vol par bloc : tout focusout suivant
   * annule le précédent.
   */
  document.addEventListener('focusout', function (e) {
    var bloc = e.target.closest('[data-range-filter]');
    if (!bloc) return;

    if (bloc._rangeCommitTimer) clearTimeout(bloc._rangeCommitTimer);

    bloc._rangeCommitTimer = setTimeout(function () {
      bloc._rangeCommitTimer = null;
      if (!bloc.isConnected) return;

      // Le point d'arrivée du focus n'est pas lisible de façon fiable
      // depuis l'event lui-même (nul au tap hors champ sous iOS Safari) :
      // on relit l'état réel du document, stable au tour de boucle
      // suivant, plutôt que de s'y fier au moment du focusout.
      if (bloc.contains(document.activeElement)) return;

      commit(bloc);
    }, 0);
  });

  /**
   * 3. Entrée : commit immédiat, sans attendre le focusout.
   */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-range-input]');
    if (!input) return;
    var bloc = input.closest('[data-range-filter]');
    if (!bloc) return;

    e.preventDefault(); // pas de soumission implicite du <form>
    if (bloc._rangeCommitTimer) {
      clearTimeout(bloc._rangeCommitTimer);
      bloc._rangeCommitTimer = null;
    }
    commit(bloc);
  });

  /**
   * 4. Bouton « Appliquer » du drawer, en phase de CAPTURE sur document.
   * Sous Safari macOS, cliquer un <button> ne déplace pas le focus : aucun
   * `focusout` n'est émis, et le listener bulle d'initFilters posé sur
   * #FilterDrawerApply (theme.js:5245-5254) lirait donc des valeurs brutes,
   * non bornées, non échangées. La capture s'exécute AVANT ce handler
   * bulle : les hidden sont normalisés ici, avant que le FormData ne soit
   * construit — aucune modification de theme.js n'est nécessaire.
   */
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target.closest('#FilterDrawerApply')) return;

      var blocs = document.querySelectorAll('#FilterDrawer [data-range-filter]');
      blocs.forEach(function (bloc) {
        if (bloc._rangeCommitTimer) {
          clearTimeout(bloc._rangeCommitTimer);
          bloc._rangeCommitTimer = null;
        }
        commit(bloc);
      });
    },
    true,
  );

  // Sécurité : ce composant ne soumet jamais son <form> nativement, la
  // requête part toujours via le `input` dispatché par commit().
  document.addEventListener('submit', function (e) {
    if (e.target.querySelector('[data-range-filter]')) e.preventDefault();
  });
})();
